import { type CreateFacilityInputType } from '@shelflix/schemas/facility/create';
import { create } from 'zustand';
import { useEffect } from 'react';
import type { Path, UseFormReturn } from 'react-hook-form';

interface FacilityFormStore {
  formData: Partial<CreateFacilityInputType>;
  setFormData: (data: Partial<CreateFacilityInputType>) => void;
  resetForm: () => void;
}

export const useFacilityFormStore = create<FacilityFormStore>((set) => ({
  formData: {},
  setFormData: (data) => set((state) => ({ formData: { ...state.formData, ...data } })),
  resetForm: () => set({ formData: {} }),
}));

export const useCepAutoFill = <
  T extends {
    cep: string;
    address: string;
    district: string;
    city: string;
    state: string;
    complement?: string;
    cityIbgeCode: string;
  },
>(
  form: UseFormReturn<T>
) => {
  useEffect(() => {
    const subscription = form.watch((values, { name }) => {
      if (name !== 'cep') return;

      const cep = values.cep?.replace(/\D/g, '');

      if (cep?.length === 8) {
        const fetchAddress = async () => {
          try {
            const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
            const data = await response.json();

            if (data.erro) {
              form.setError('cep' as Path<T>, { message: 'CEP inválido ou não encontrado' });
              return;
            }

            form.clearErrors('cep' as Path<T>);

            form.setValue('address' as Path<T>, data.logradouro ?? '');
            form.setValue('district' as Path<T>, data.bairro ?? '');
            form.setValue('city' as Path<T>, data.localidade ?? '');
            form.setValue('state' as Path<T>, data.uf ?? '');
            form.setValue('cityIbgeCode' as Path<T>, data.ibge ?? '');
          } catch (_err) {
            form.setError('cep' as Path<T>, { message: 'Erro ao buscar o endereço' });
          }
        };

        fetchAddress();
      }
    });

    return () => subscription.unsubscribe();
  }, [form]);
};
